import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
    IEntityPreview,
    IEntityRecord,
    IPreviewProgram,
} from '../../components/Entity/interfaces'
import { IEntityInstanceRelationsResponse } from '../../services/apiInterfaces'
import {
    fetchCloumnsOrderForPreview,
    fetchRecordDataByIdForPreview,
    fetchRecordRelationDataByID,
} from '../../services/entityServices'
import {
    EntityStatus,
    ShipmentAttributes,
    temperatureUnits,
    widgetView,
} from '../../constants/constants'
import { IAlarmStatus } from '../../components/Applications/Intransit/interfaces'
import { isEmptyObject } from '../../utils/StringUtils'
import { fetchRelationsForGuest } from '../../services/guestServices'
import { fetchRelations } from '../../services/dtServices'
import { IRelation, IRuleSet } from '../../components/DTStudio/interfaces'
import { ColumnOrderListType } from './entitySlice'

interface EntityViewData {
    entityRelationsData: any[]
    entityParentRelationsData: any[]
    // businessRulesData: string[] //TODO: Remove this completely in API. Already removed from UI
    edges: any[]
    nodes: any[]
    userActionsData: any[]
    relatedEntitiesResponse: IEntityInstanceRelationsResponse
    selfEntityAttributeData: IEntityRecord
    entityInstanceCreatedDate: string
    entityInstanceEndDate: string
    earliestDataTime: string
    //TODO: Check if this ruleSetId is used anywhere else
    ruleSetId: string[] // TODO: use ruleSetIds from ruleSets in this object
    status: EntityStatus
    action: string
    alarmStatus: IAlarmStatus | undefined
    templateName: string
    shipmentStatus: string
    enabledApplications: string[]
    ruleSets?: IRuleSet[]
    organizationId: string
    program: IPreviewProgram | null
}
export enum ReportGenerationStatus {
    IDLE = 'IDLE',
    IN_PROGRESS = 'IN_PROGRESS',
    SUCCESS = 'SUCCESS',
    FAILED = 'FAILED',
}
export interface IReportGeneration {
    reportGenerationStatus: ReportGenerationStatus
    reportEntityInstanceId?: string // to store entityInstanceId of generated report of shipment/asset temporarily.
}
export interface EntityViewState {
    viewTab: string
    entityData: EntityViewData
    entityTemplateRelations: IRelation[]
    columnsOrder: ColumnOrderListType[]
    isLoadingEntityRelations: boolean
    isLoadingEntityData: boolean
    error: string
    reportGeneration: IReportGeneration
    guestUserPreferredUnit: string
}
export const defaultTab = widgetView.GRAPH_APACHE_ECHART_VIEW
const initialState: EntityViewState = {
    viewTab: defaultTab,
    entityData: {} as unknown as EntityViewData,
    entityTemplateRelations: [],
    columnsOrder: [],
    isLoadingEntityRelations: false,
    isLoadingEntityData: false,
    error: '', //preview page error, if error occurs from API, we are navigativing to repective home page(based on isGustUser)
    reportGeneration: {
        reportGenerationStatus: ReportGenerationStatus.IDLE,
        reportEntityInstanceId: undefined,
    },
    guestUserPreferredUnit:
        localStorage.getItem('selectedTempUnit') ?? temperatureUnits[0],
}

export const getEntityViewDetails = createAsyncThunk(
    'entity/getEntityViewDetails',
    async (
        {
            orgId,
            recordId,
            entityTemplateId,
            isGuestUser,
            guestUserPreferredUnit,
        }: {
            orgId: string
            recordId: string
            entityTemplateId: string
            isGuestUser: boolean
            guestUserPreferredUnit?: string
        },
        thunkAPI,
    ) => {
        try {
            const [previewResponse, relatedEntitiesResponse]: [
                IEntityPreview,
                IEntityInstanceRelationsResponse,
            ] = await Promise.all([
                fetchRecordDataByIdForPreview(
                    orgId,
                    recordId,
                    isGuestUser,
                    guestUserPreferredUnit,
                ),
                fetchRecordRelationDataByID(
                    orgId,
                    entityTemplateId,
                    recordId,
                    false,
                    isGuestUser,
                    guestUserPreferredUnit,
                ),
            ])

            if (isEmptyObject(previewResponse)) {
                return thunkAPI.rejectWithValue('No data found')
            }

            const entityRelationsData = previewResponse?.relations?.map(
                (item) => ({
                    ...item,
                    isRelation: true,
                    label: item.uid,
                    viewID: recordId,
                }),
            )

            const entityParentRelationsData =
                previewResponse?.parentRelations?.map((item: any) => ({
                    ...item,
                    isRelation: true,
                    label: item.uid,
                    viewID: recordId,
                })) ?? []

            const updatedEdges =
                previewResponse.workflow?.edges?.map((item, index) => ({
                    id: index + 1,
                    source: item.src,
                    target: item.dest,
                })) ?? []

            const updatedNodes =
                previewResponse.workflow?.nodes?.map((item: any) => ({
                    id: item.id?.toString(),
                    templateName: item.templateName || '',
                    position: {
                        x: Number(item.position?.x),
                        y: Number(item.position?.y),
                    },
                    data: {
                        label: item.label,
                        relationId: item.relationId,
                        logo: item.logo,
                        status: item.status,
                    },
                    type: 'logoNode',
                })) ?? []

            const userActionsData =
                previewResponse?.actions?.available_actions ?? []

            const selfEntityUid: string = previewResponse?.attributes?.uid || ''

            const selfEntityAttributeData: IEntityRecord = {
                attributes: previewResponse?.attributes?.attributes ?? [],
                entityId: recordId,
                uid: selfEntityUid,
                templateId: entityTemplateId,
                action: previewResponse.action,
                templateName: previewResponse.templateName,
                status: previewResponse.status,
                organizationId: previewResponse.organizationId,
            }

            const shipmentStatus =
                previewResponse?.attributes?.attributes.find(
                    (i) =>
                        i.id === ShipmentAttributes.ShipmentStatusAttributeId,
                )?.value || ''

            return {
                entityRelationsData,
                entityParentRelationsData,
                edges: updatedEdges,
                nodes: updatedNodes,
                userActionsData,
                relatedEntitiesResponse,
                selfEntityAttributeData,
                entityInstanceCreatedDate: previewResponse.instanceCreatedDate,
                entityInstanceEndDate: previewResponse.instanceEndDate,
                earliestDataTime:
                    previewResponse.earliestDataTime ||
                    previewResponse.instanceCreatedDate,
                ruleSetId: previewResponse.ruleSetId,
                status: previewResponse.status,
                action: previewResponse.action,
                alarmStatus: previewResponse.alarmStatus,
                templateName: previewResponse.templateName,
                shipmentStatus,
                enabledApplications: previewResponse.enabledApplications ?? [],
                ruleSets: previewResponse.ruleSets,
                organizationId: previewResponse.organizationId,
                program: previewResponse.program ?? null,
            }
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'No data found'
            return thunkAPI.rejectWithValue(errorMessage)
        }
    },
)

export const getEntityRelations = createAsyncThunk(
    'entity/getEntityRelations',
    async (
        {
            orgId,
            entityTemplateId,
            isGuestUser,
        }: {
            orgId: string
            entityTemplateId: string
            isGuestUser: boolean
        },
        thunkAPI,
    ) => {
        try {
            const response = isGuestUser
                ? await fetchRelationsForGuest(entityTemplateId, orgId)
                : await fetchRelations(entityTemplateId, orgId)
            return response
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        }
    },
)

export const getEntityPreviewColumnsOrder = createAsyncThunk(
    'entity/getEntityPreviewColumnsOrder',
    async (
        {
            orgId,
            entityTemplateId,
            isGuestUser,
            isAdmin,
        }: {
            orgId: string
            entityTemplateId: string
            isGuestUser: boolean
            isAdmin: boolean
        },
        thunkAPI,
    ) => {
        try {
            const response = await fetchCloumnsOrderForPreview(
                entityTemplateId,
                orgId || '',
                isGuestUser,
            )
            let columnsOrder = response?.map((i) => {
                return {
                    ...i,
                    id: i.attributeName,
                    name: i.columnName,
                    selected: i.columnSelection,
                }
            })
            const localStorageColumnsOrder = localStorage.getItem(
                `previewColumnsOrder_${isGuestUser ? 'guestOrg' : orgId}_${entityTemplateId}`,
            )
            if (localStorageColumnsOrder && !isAdmin) {
                columnsOrder =
                    JSON.parse(localStorageColumnsOrder) || columnsOrder
            }
            return columnsOrder
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        }
    },
)

const entityViewSlice = createSlice({
    name: 'entityViewSlice',
    initialState,
    reducers: {
        updateViewTab(state, action) {
            state.viewTab = action.payload
        },
        updateColumnsOrder(state, action) {
            state.columnsOrder = action.payload
        },
        updateReportGenerationDetails(
            state,
            action: PayloadAction<IReportGeneration>,
        ) {
            /**
             * To store entityInstanceId of generated report of shipment/asset temporarily.
             * This is stored only on the first time report is generated.
             * We will reset it to empty string once we navigate away from preview page. Because when we
             * navigate back to preview page, we should be getting the same entityInstanceId from preview API again.
             */
            state.reportGeneration = action.payload
        },
        updateGuestUserPreferredUnit(state, action) {
            state.guestUserPreferredUnit = action.payload
            localStorage.setItem('selectedTempUnit', action.payload)
        },
        resetEntityView(state) {
            state.viewTab = initialState.viewTab
            state.entityData = initialState.entityData
            state.entityTemplateRelations = initialState.entityTemplateRelations
            state.columnsOrder = initialState.columnsOrder
            state.error = initialState.error
            state.reportGeneration = initialState.reportGeneration
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getEntityViewDetails.pending, (state) => {
                state.isLoadingEntityData = true
            })
            .addCase(getEntityViewDetails.fulfilled, (state, action) => {
                state.entityData = action.payload
                state.error = ''
                state.isLoadingEntityData = false
            })
            .addCase(getEntityViewDetails.rejected, (state, action) => {
                state.entityData = {} as unknown as EntityViewData
                state.error = action.payload as string
                state.isLoadingEntityData = false
            })
        builder
            .addCase(getEntityRelations.pending, (state) => {
                state.isLoadingEntityRelations = true
            })
            .addCase(getEntityRelations.fulfilled, (state, action) => {
                state.entityTemplateRelations = action.payload
                state.isLoadingEntityRelations = false
            })
            .addCase(getEntityRelations.rejected, (state) => {
                state.entityTemplateRelations = []
                state.isLoadingEntityRelations = false
            })
        builder
            .addCase(
                getEntityPreviewColumnsOrder.fulfilled,
                (state, action) => {
                    state.columnsOrder = action.payload
                },
            )
            .addCase(getEntityPreviewColumnsOrder.rejected, (state) => {
                state.columnsOrder = []
            })
    },
})

export const {
    updateViewTab,
    updateColumnsOrder,
    updateReportGenerationDetails,
    updateGuestUserPreferredUnit,
    resetEntityView,
} = entityViewSlice.actions

export default entityViewSlice.reducer
